import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../../components/ButtonComponent";
import InputComponent from "../../../components/InputComponent";
import Routes from "../../../helpers/Routes";
import Toast from "../../../helpers/Toast";
import { Utilities } from "../../../helpers/Utilities";
import { AssistanceDTO } from "../../../models/Assistance/AssistanceDTO";
import { CreateAssistanceDTO, CreateAssistanceDTOSchema } from "../../../models/Assistance/CreateAssistanceDTO";
import { AssistanceService } from "../../../services/AssistanceService";
import { EquipmentService } from "../../../services/EquipmentService";
import { useIntl } from "react-intl";
import SelectComponent from "../../SelectComponent";
import SearchSelectComponent from "../../SearchSelectComponent";
import { SearchByProject } from "../../../models/Project/SearchByProject";
import { ProjectService } from "../../../services/ProjectService";
import { EquipmentDTO } from "../../../models/Equipment/EquipmentDTO";
import { ProjectDTO } from "../../../models/Project/ProjectDTO";
import { useAvailableModulesContext } from "../../../context/AvailableModulesContext";
import { useAuth } from "../../../context/AuthContext";
import { TimeHistoryDTO } from "../../../models/TimeHistory/TimeHistoryDTO";
import { RenderCountersInputs } from "../RenderCountersInputs";

export type SelectItemsProps = {
    label: string;
    value: string;
};

export default function AssistanceWorkshopCreateA() {
    //Pagina
    const { equipmentErpId } = useParams<{ equipmentErpId: string }>();
    const [isLoadingAssistance, setIsLoadingAssistance] = useState<boolean>(false);
    const assistanceService = new AssistanceService();
    const equipmentService = new EquipmentService();
    const projectService = new ProjectService();

    const [equipmentNotFound, setEquipmentNotFound] = useState<boolean>(false);
    const [assistance, setAssistance] = useState<AssistanceDTO>({} as AssistanceDTO);

    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [isLoadingUpdateAssistance, setIsLoadingUpdateAssistance] = useState<boolean>(false);
    const [equipment, setEquipment] = useState<EquipmentDTO>({} as EquipmentDTO);
    const [project, setProject] = useState<ProjectDTO>({} as ProjectDTO);
    const [isSearchProjectLoading, setIsSearchProjectLoading] = useState<boolean>(false);
    const [selectedAssistanceType, setSelectedAssistanceType] = useState<string>(Utilities.LoadParameterFromURLQuery("assistanceType", "string", ""));
    const [assistanceTypesComponent, setAssistanceTypesComponent] = useState<SelectItemsProps[]>([]);

    const [isActiveFabricationModule, setIsActiveFabricationModule] = useState<boolean>(false);

    const [timeHistory, setTimeHistory] = useState<TimeHistoryDTO>({ counter1: 0, counter2: 0, counter3: 0 } as TimeHistoryDTO);

    const { availableModules, assistanceTypes, counterOptions } = useAvailableModulesContext();
    const [isActiveProjectModule, setIsActiveProjectModule] = useState<boolean>(false);
    const [isActiveCountersModule, setIsActiveCountersModule] = useState<boolean>(false);
    const [isActiveCarPlate, setIsActiveCarPlate] = useState<boolean>(false);
    const [isActiveErpSupportModule, setIsActiveErpSupportModule] = useState<boolean>(false);


    const [projectSelected, setProjectSelected] =
        useState<SelectItemsProps | null>(null);

    // Separate state variables for date and time
    const [assistanceDate, setAssistanceDate] = useState<string>(Utilities.formatDateYYYYMMDD(new Date()));
    const [assistanceTime, setAssistanceTime] = useState<string>(Utilities.GetTimeFromDateTime(new Date()));

    //obter tipo de assistencia
    const getAssistanceTypes = async () => {
        const list: SelectItemsProps[] = [];

        assistanceTypes.forEach((element) => {
            var item: SelectItemsProps = {
                value: element,
                label: element,
            };
            list.push(item);
        });

        setAssistanceTypesComponent(list);
    };

    //modulos ativos
    useEffect(() => {
        if (availableModules.includes("ProjectSage50cIsActiveProjectModule")) {
            setIsActiveProjectModule(true);
        }
        if (availableModules.includes("FabricationSage50cIsActiveFabricationModule")) {
            setIsActiveFabricationModule(true);
        }
        if (availableModules.includes("IsActiveCountersModule")) {
            setIsActiveCountersModule(true);
        }
        if (availableModules.includes("IsActiveCarPlate")) {
            setIsActiveCarPlate(true);
        }
        if (availableModules.includes("IsActiveErpSupportModule")) {
            setIsActiveErpSupportModule(true);
        }
    }, [availableModules]);


    //obra
    const SearchInputByProject = async () => {
        if (projectSelected === null) {
            setProject({} as ProjectDTO);
            setIsSearchProjectLoading(false);
            return;
        }

        var data: SearchByProject = {
            project: projectSelected.label || "",
        };

        const response = await projectService.GetByProject(data);

        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
            setIsSearchProjectLoading(false);
            return [];
        }
        setProject(response.obj[0]);
        setIsSearchProjectLoading(false);
    };

    useEffect(() => {
        getAssistance();
        getAssistanceTypes();
    }, []);

    useEffect(() => {
        if (projectSelected != null) {
            SearchInputByProject();
        }
    }, [projectSelected]);

    // Update date/time state when assistance data changes
    useEffect(() => {
        if (assistance?.date) {
            setAssistanceDate(Utilities.formatDateYYYYMMDD(assistance.date));
            setAssistanceTime(Utilities.GetTimeFromDateTime(assistance.date));
        }
    }, [assistance?.date]);

    const getAssistance = async () => {
        setIsLoadingAssistance(true);
        var response = await equipmentService.GetByIdErp(equipmentErpId ?? "0");
        setIsLoadingAssistance(false);

        if (response.success !== true || response.obj == null) {
            setEquipmentNotFound(true);
            Toast.Show("error", response.message);
            return;
        }

        setAssistance({
            ...assistance,
            equipment: response.obj,
            date: new Date(),
        });
        //Por defeito o estado é inicializado no 1,
        //por isso não é necessario fazer a mesma coisa de cima
    };

    const createAssistance = async () => {
        // Combine date and time into a single Date object
        const combinedDateTime = new Date(`${assistanceDate}T${assistanceTime}`);

        var object: CreateAssistanceDTO = {
            id: assistance.id,
            date: combinedDateTime,
            description: assistance.description,
            notes: assistance.notes,
            equipmentErpId: assistance.equipment != null ? assistance.equipment.erpId : "",
            equipmentName: equipment.name != undefined ? equipment.name : null,
            equipmentCarPlate: equipment.erpId != null ? equipment.carPlate : null,
            equipmentNumSerie: equipment.erpId != null ? equipment.serialNumber : null,
            equipmentBrand: equipment.erpId != null ? equipment.brand : null,
            equipmentModel: equipment.erpId != null ? equipment.model : null,
            projectErpId: projectSelected != null ? projectSelected.value : null,
            clientErpId: assistance?.equipment?.client?.erpId ?? null,
            typeDoc: null,
            numDoc: null,
            serie: null,
            clientId: assistance?.equipment?.client?.id ?? null,
            counter1: timeHistory.counter1 || 0,
            counter2: timeHistory.counter2 || 0,
            counter3: timeHistory.counter3 || 0,
            unit1: timeHistory.unit1,
            unit2: timeHistory.unit2,
            unit3: timeHistory.unit3,
            assistanceType: selectedAssistanceType || "Reparação",
            stockEntryDocumentErpId: null,
            budgetSellingPrice: assistance.budgetSellingPrice ?? null,
            budgetCostPrice: assistance.budgetCostPrice ?? null,
        };


        var responseValidate = CreateAssistanceDTOSchema.validate(object, {
            allowUnknown: true,
        });

        if (responseValidate.error != null) {
            var message = responseValidate.error!.message;
            Toast.Show("error", message);
            return false;
        }

        // Verificar se o módulo da fabricação está visível e o campo "Tipo de Assistência" está vazio
        if (isActiveFabricationModule && !selectedAssistanceType) {
            Toast.Show("error", "Selecione o campo 'Tipo de Assistência'.");
            return false;
        }


        setIsLoadingUpdateAssistance(true)
        setIsSearchProjectLoading(true)

        var response = await assistanceService.CreateInDbAndErp(object);
        setIsSearchProjectLoading(false)
        setIsLoadingUpdateAssistance(false)

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return false;
        }

        Toast.Show("success", intl.formatMessage({ id: "Screens.Assistances.Create.Success" }));

        if (currentUser?.preferedView === "Backoffice") {
            navigate(`${Routes.backoffice.details}/${response.obj}`);
        } else {
            navigate(`${Routes.oficina.details}/${response.obj}`);
        }
        return true;
    };

    //método Obra
    const GetProject = async (
        value: string
    ): Promise<SelectItemsProps[]> => {
        var data: SearchByProject = {
            project: value,
        };

        var response = await projectService.GetByProject(data);
        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        const list: SelectItemsProps[] = [];

        response.obj.forEach((element) => {
            var item: SelectItemsProps = {
                value: element.erpId,
                label: element.description,
            };
            list.push(item);
        });

        return list;
    };

    const intl = useIntl();

    return (
        <>
            {/* Se o pedido para obter a assistencia não retornou nada */}
            {equipmentNotFound === true && (
                <div>
                    <span className="error">O equipamento selecionado não foi encontrado.</span>
                </div>
            )}

            <Row style={{ margin: "0px", padding: "0px" }}>
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={6}
                    sm={6}
                    xs={6}
                    style={{ width: "100%", height: "43px" }}
                    label={"Tipo de Assistência"}
                    selectedItemValue={selectedAssistanceType}
                    items={assistanceTypesComponent}
                    onChange={(t: string) => {
                        setSelectedAssistanceType(t);
                    }}
                />
            </Row >

            <Row style={{ margin: "0px", padding: "0px" }}>
                {isActiveProjectModule && (
                    <>
                        {/* Mostrar componentes quando projectModuleValue for verdadeiro */}
                        <SearchSelectComponent
                            xl={6}
                            lg={6}
                            md={6}
                            sm={6}
                            xs={6}
                            label={"Nome Obra"}
                            loadOptions={GetProject}
                            onChange={(e) => {
                                setProjectSelected(e);
                            }}
                            value={projectSelected}
                            style={{ width: "100%" }}
                        />

                    </>
                )}
            </Row>

            {/*Estado 1 - data + equipamento + cliente*/}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6}
                    lg={6}
                    md={6}
                    sm={6}
                    xs={6}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={
                        project?.client?.name !== undefined
                            ? project?.client?.name
                            : assistance?.equipment?.client?.name
                    }
                    label={"Cliente"}
                    readOnly={true}
                    showLoadingSkeleton={isLoadingAssistance}
                />
            </Row>

            {isActiveCarPlate && (
                <Row style={{ margin: "0px", padding: "0px" }}>
                    <InputComponent
                        xl={2}
                        lg={3}
                        md={5}
                        sm={6}
                        xs={6}
                        style={{ width: "100%" }}
                        type={"text"}
                        value={assistance?.equipment?.carPlate}
                        label={"Matrícula"}
                        readOnly={true}
                        showLoadingSkeleton={isLoadingAssistance}
                    />
                </Row >
            )}

            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={2}
                    lg={3}
                    md={5}
                    sm={6}
                    xs={6}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={assistance?.equipment?.serialNumber}
                    label={"Nº Série"}
                    readOnly={true}
                    showLoadingSkeleton={isLoadingAssistance}
                />
                <InputComponent
                    xl={2}
                    lg={3}
                    md={5}
                    sm={6}
                    xs={6}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={assistance?.equipment?.brand}
                    label={"Marca"}
                    readOnly={true}
                    showLoadingSkeleton={isLoadingAssistance}
                />

                <InputComponent
                    xl={2}
                    lg={3}
                    md={5}
                    sm={6}
                    xs={6}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={assistance?.equipment?.model}
                    label={"Modelo"}
                    readOnly={true}
                    showLoadingSkeleton={isLoadingAssistance}
                />
            </Row>

            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={2}
                    lg={3}
                    md={5}
                    sm={6}
                    xs={6}
                    style={{ width: "100%" }}
                    type={"date"}
                    value={assistanceDate}
                    label={"Dia"}
                    readOnly={false}
                    showLoadingSkeleton={isLoadingAssistance}
                    onChange={(t: string) => {
                        setAssistanceDate(t);
                    }}
                />

                <InputComponent
                    xl={2}
                    lg={3}
                    md={5}
                    sm={6}
                    xs={6}
                    style={{ width: "100%" }}
                    type={"time"}
                    value={assistanceTime}
                    label={"Hora"}
                    readOnly={false}
                    showLoadingSkeleton={isLoadingAssistance}
                    onChange={(t: string) => {
                        setAssistanceTime(t);
                    }}
                />
            </Row>

            {/* Estado 2 - Contadores */}
            {
                isActiveCountersModule && !isActiveErpSupportModule && (
                    <>
                        <label style={{ margin: "0px", padding: "13px", fontWeight: "bold" }}>
                            {intl.formatMessage({ id: "Screens.AssistanceCreateB.CountersTitle" })}
                        </label>
                        <Row style={{ margin: "0px", padding: "0px" }}>
                            {RenderCountersInputs(counterOptions, timeHistory, setTimeHistory)}
                        </Row>
                    </>
                )
            }

            {/* Estado 3 - Descrições */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6}
                    lg={9}
                    md={10}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    type={"textArea"}
                    value={assistance.description}
                    label={"Descrição do Serviço"}
                    readOnly={false}
                    onChange={(t: string) => {
                        setAssistance({ ...assistance, description: t });
                    }}
                />
            </Row>
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6}
                    lg={9}
                    md={10}
                    sm={12}
                    xs={12}
                    style={{ width: "100%" }}
                    type={"textArea"}
                    value={assistance.notes}
                    label={"Observações"}
                    readOnly={false}
                    onChange={(t: string) => {
                        setAssistance({ ...assistance, notes: t });
                    }}
                />
            </Row>

            {/* Estado 4 - Ativar Assistência */}
            <Row style={{ margin: "1rem 0px 0px 0px" }}>
                <Col xl={2} lg={6} md={6} sm={12} xs={12}>
                    <ButtonComponent
                        text={intl.formatMessage({ id: "AssistanceWorkshopCreateA.CreateAssistance" })}
                        style={{ width: "100%", height: "3em" }}
                        //faltava esta propriedade
                        loading={isSearchProjectLoading}
                        onClick={createAssistance}

                        disabled={equipmentNotFound === true || isLoadingUpdateAssistance === true && isSearchProjectLoading === true}
                    />
                </Col>
            </Row>

        </>
    );
}
